import { rgba } from 'polished';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Route } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Button, Callout, Card, Heading3, Paragraph, Popover, tokens } from '@treams/treams-ui';

import ApiTokens from '../settings/api-tokens/pages/ApiTokens';
import ConnectorDryRun from './ConnectorDryRun';
import ConfigurationField from './fields/ConfigurationField';

const FieldCard = styled(Box)<{ error?: boolean }>`
	border: 1px solid ${(props) => (props.error ? props.theme.colors.red : tokens.border.subtle)};
	box-shadow: 0 0 0 2px
		${(props) => rgba(props.error ? props.theme.colors.red : tokens.border.subtle, 0.25)};
	border-radius: 5px;
`;

export type FieldLabels = {
	title: string;
	description: string;
	confirmTitle?: string;
	confirmDescription?: string;
};

export const SharedFields: { [key: string]: FieldLabels } = {
	goLiveDate: {
		title: 'Go-live datum',
		description: 'Selecteer de datum dat de medewerkers toegang krijgen tot Treams.',
	},

	syncAvatars: {
		title: 'Avatars synchroniseren',
		description:
			'Wil je de avatar (profielfoto) van de medewerker synchroniseren via de koppeling? Medewerkers kunnen hun avatar dan niet meer handmatig aanpassen.',
	},

	'sso.sync': {
		title: 'Gebruik eenmalig inloggen (SSO)',
		description: 'Let op: Configureer de SSO via Beheer > Integraties > Eenmalig inloggen (SSO).',
	},

	syncLocale: {
		title: 'Taal synchroniseren',
		description:
			'Wil je de taal van de medewerker synchroniseren via de koppeling? Medewerkers kunnen hun taal dan niet meer handmatig aanpassen.',
	},

	defaultLocale: {
		title: 'Standaard taal',
		description:
			'Selecteer een taal die wordt gebruikt bij nieuwe medewerkers wanneer er geen is ingesteld bij de medewerker.',
	},

	syncJobs: {
		title: 'Functies synchroniseren',
		description: 'Wil je de functies van medewerker synchroniseren via de koppeling?',
	},

	syncManagers: {
		title: 'Managementrechten synchroniseren',
		description: 'Wil je managementrechten synchroniseren via de koppeling?',
	},

	managersIndirect: {
		title: 'Managementrechten indirect toepassen',
		description: 'Wil je dat managers inzage hebben in onderliggende afdelingen?',
	},

	employeeFilter: {
		title: 'Kies welke medewerkers in Treams verschijnen',
		description:
			'Wil je geen medewerkers synchroniseren? Als je dit inschakelt, wordt alleen de organisatiestructuur gesynchroniseerd.',
	},

	dryRun: {
		title: 'Test-modus',
		description:
			'Zet dit aan om te bekijken welke wijzigingen in het systeem woorden doorgevoerd zonder de wijzigingen daadwerkelijk uit te voeren.',
	},

	'twoWay.locale': {
		title: 'Taal tweeweg-koppeling',
		description: 'Selecteer in welke taal de bestanden voor de tweeweg-koppeling worden geüpload.',
	},

	scimAllowAccountReuseOnExternalID: {
		title: 'Hergebruik account op basis van externalID',
		description: 'Heractiveer gearchiveerde accounts wanneer externalID overeenkomt.',
		confirmTitle: 'product.configuration.scim.externalId.title',
		confirmDescription: 'product.configuration.scim.externalId.description',
	},

	scimEnableGroupWithoutWorkspaceRole: {
		title: 'SCIM Groep',
		description: 'Toon SCIM Group voor gebruikersaccounts zonder bijzondere rol.',
	},
};

const FieldStatus = styled(Box)<{ error?: boolean }>`
	flex-grow: 0;
	flex-shrink: 0;
	border-radius: 4px;

	background: ${(props) =>
		rgba(props.error ? props.theme.colors.red : props.theme.colors.green, 0.25)};
	color: ${(props) => (props.error ? props.theme.colors.red : props.theme.colors.greenDark)};
`;

export function FieldContainer(
	props: PropsWithChildren<{
		field: graphql.ConnectorConfigField;
		translate: (key: string) => { title: string; description: string } | undefined;
	}>,
) {
	const { t } = useTranslation();

	return (
		<FieldCard key={props.field.key} mb={3} p={3} error={!props.field.isConfigured}>
			<Flex alignItems="flex-start">
				<Box flexGrow={1} flexShrink={1}>
					<Heading3>{props.translate(props.field.key)?.title}</Heading3>
					<Paragraph>{props.translate(props.field.key)?.description}</Paragraph>
				</Box>
				<FieldStatus error={!props.field.isConfigured} px={3} py={2} ml={2}>
					{props.field.isConfigured
						? t('connectors.settings.configuration.configured')
						: t('connectors.settings.configuration.required')}
				</FieldStatus>
			</Flex>
			<Box mt={2}>{props.children}</Box>
		</FieldCard>
	);
}

FieldContainer.Skeleton = function FieldContainerSkeleton() {
	return (
		<Card.Skeleton>
			<Flex alignItems="flex-start">
				<Box flexGrow={1} flexShrink={1}>
					<Heading3.Skeleton mb={2} />
					<Paragraph.Skeleton lines={2} />
				</Box>
			</Flex>
		</Card.Skeleton>
	);
};

export default function ProductConfiguration(props: {
	connector: graphql.Connector;
	translate: (key: string) => FieldLabels | undefined;
	documentationLink?: string;
}) {
	const { t } = useTranslation();

	const fields = props.connector.config;

	const [update] = graphql.useUpdateProductConfigurationMutation();

	return (
		<Box>
			{props.documentationLink && (
				<Callout color="blue">
					<a href={props.documentationLink} target="_blank" rel="noreferrer noopener">
						{t('connectors.settings.configuration.callout')}
					</a>
				</Callout>
			)}
			<Heading3 my={3}>{t('connectors.settings.configuration.title')}</Heading3>
			{props.connector.type !== graphql.ConnectorType.Scim && (
				<Callout
					color="green"
					mb={3}
					title={t('connectors.settings.configuration.dryRun.banner.title')}
					description={t('connectors.settings.configuration.dryRun.banner.description')}
				>
					<Box flexGrow={1} />
					<Box flexGrow={0} flexShrink={0}>
						<Button
							type="secondary"
							title={t('connectors.settings.configuration.dryRun.openButton.title')}
							link={`/settings/integrations/${props.connector.type.toLowerCase()}/results/groups`}
						/>
					</Box>
				</Callout>
			)}
			{fields.map((field) => (
				<FieldContainer key={field.id} field={field} translate={props.translate}>
					<ConfigurationField
						connectorType={props.connector.type}
						translate={props.translate}
						field={field}
						onSave={async (input) => {
							await update({
								variables: {
									input: {
										...input,
										key: field.key,
										connectorType: props.connector.type,
									},
								},
							});
						}}
					/>
				</FieldContainer>
			))}

			{props.connector.type === graphql.ConnectorType.Scim && <ApiTokens type="SCIM" />}

			<Route path="/settings/integrations/:id/results/:type">
				{({ match }) => (
					<Popover center visible={!!match}>
						<ConnectorDryRun type={props.connector.type} />
					</Popover>
				)}
			</Route>
		</Box>
	);
}
